import { useState } from 'react';
import { Circle, CheckCircle2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCompletarTarea, useCrearTarea } from '@/hooks/useTareas';
import type { TareaDetalle } from '@/types/tareas';
import { AvatarsAsignados, PrioridadBadge, VencimientoTexto } from '@/components/tareas/TareaPiezas';

const campo = 'px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300';

/** Las subtareas son tareas completas de un solo nivel; abrir una la muestra en este mismo modal. */
export function SubtareasPanel({ tarea, onAbrirTarea }: { tarea: TareaDetalle; onAbrirTarea: (id: string) => void }) {
  const crear = useCrearTarea();
  const completar = useCompletarTarea();
  const [titulo, setTitulo] = useState('');

  const total = tarea.subtareasLista.length;
  const hechas = tarea.subtareasLista.filter(s => s.completadaAt).length;
  const porcentaje = total ? Math.round((hechas / total) * 100) : 0;

  const agregar = (e: React.FormEvent) => {
    e.preventDefault();
    const valor = titulo.trim();
    if (!valor) return;
    crear.mutate({ titulo: valor, tableroId: tarea.tableroId, parentId: tarea.id }, { onSuccess: () => setTitulo('') });
  };

  return (
    <div className="space-y-4">
      {total > 0 && (
        <div>
          <div className="flex justify-between text-xs text-text-secondary mb-1">
            <span>{hechas} de {total} completadas</span><span>{porcentaje}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${porcentaje}%` }} />
          </div>
        </div>
      )}

      {total === 0 && <p className="text-sm text-text-secondary">Sin subtareas. Úsalas cuando un paso necesite su propio responsable o fecha.</p>}

      <ul className="space-y-1.5">
        {tarea.subtareasLista.map(s => (
          <li
            key={s.id}
            onClick={() => onAbrirTarea(s.id)}
            className="flex items-center gap-3 rounded-lg border border-border bg-white px-3 py-2 hover:border-primary-300 cursor-pointer"
          >
            <button
              onClick={e => { e.stopPropagation(); completar.mutate({ id: s.id, completar: !s.completadaAt }); }}
              className="text-gray-300 hover:text-emerald-500 flex-shrink-0"
              aria-label={s.completadaAt ? 'Reabrir subtarea' : 'Completar subtarea'}
            >
              {s.completadaAt ? <CheckCircle2 size={20} className="text-emerald-500" /> : <Circle size={20} />}
            </button>
            <span className={cn('flex-1 min-w-0 text-sm truncate', s.completadaAt ? 'line-through text-text-secondary' : 'text-text-primary font-medium')}>
              {s.titulo}
            </span>
            <PrioridadBadge prioridad={s.prioridad} className="hidden sm:inline-flex" />
            <VencimientoTexto tarea={s} className="hidden sm:inline-flex" />
            <AvatarsAsignados asignados={s.asignados} />
          </li>
        ))}
      </ul>

      <form onSubmit={agregar} className="flex gap-2">
        <input value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={255} placeholder="Nueva subtarea" className={cn(campo, 'flex-1 min-w-0')} />
        <button type="submit" disabled={!titulo.trim() || crear.isPending} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-medium disabled:opacity-50">
          <Plus size={14} /> Agregar
        </button>
      </form>
    </div>
  );
}
